//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect } from 'react';
import { useState, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useParams } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { logPageView } from '../../system/firebase';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { BRAND_PAGE_TAB_ID_JOBS, PAGE_TITLE_BRAND_PAGE } from '../../common/constants';
import { ELASTICSEARCH_SORT_COLUMN_UPDATE_DATE, ELASTICSEARCH_SORT_ORDER_DESC } from '../../common/elasticSearchHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
// import { loadActiveJobsByEmployerId } from '../../common/services/brandPageServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../../common/elemental/AfitdPageHead';
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import ActiveJobsSideBar from '../../jobCenter/subComponents/Sidebar';
import JobDetail from '../../jobCenter/subComponents/JobDetail';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import BrandPageHeader from './subComponents/Header';
import CompanyInfo from './CompanyInfo';
import CompanyBioBenefit from './CompanyBioBenefit';
import Badge from '@material-ui/core/Badge';
import CompanyShowcase from './CompanyShowcase';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
// import _size from 'lodash/size';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = params => {
    const { isBannerVisible, isLogoVisible, isActiveJobsView } = params;

    return makeStyles(theme => ({
        root: { paddingTop: isBannerVisible ? 0 : 16 },
        pageContainer: {
            backgroundColor: '#FAFAFC',
            overflow: 'hidden',
            height: '100%',
            minHeight: '100%',
            flexWrap: 'unset',
        },
        pageHeader: { maxHeight: '81px' },
        pageContent: {
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingLeft: '64px',
            paddingRight: '64px',
        },
        jobCenterLeft: { height: '100%', paddingTop: '25px' },
        jobCenterRight: {
            height: '100%',
            overflowY: 'hidden',
            flexWrap: 'unset',
            paddingTop: '35px',
            minHeight: '600px',
        },
        tier3Buttons: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginTop: isBannerVisible && isLogoVisible ? '-45px' : 24,
        },
        infoButton: {
            marginRight: '5px',
            fontSize: '15px',
            fontWeight: 'bold',
            backgroundColor: isActiveJobsView ? '#00000000' : '#F0F2F6',
            color: !isActiveJobsView ? 'black' : 'rgba(17, 30, 20, 0.4)',
        },
        jobButton: {
            marginLeft: '5px',
            fontSize: '15px',
            fontWeight: 'bold',
            backgroundColor: !isActiveJobsView ? '#00000000' : '#F0F2F6',
            color: isActiveJobsView ? 'black' : 'rgba(17, 30, 20, 0.4)',
        },
        jobNumberBadge: {
            marginLeft: '20px',
            marginRight: '10px',
        },
        companyProfileSection: {
            display: isActiveJobsView && 'none',
            marginTop: 32,
        },
        companyActiveJobsSection: {
            display: !isActiveJobsView && 'none',
            marginTop: 32,
        },
        jobInfoContainerSection: { width: '100%' },
        navigation: { padding: '0px 0px 16px 20px', [theme.breakpoints.down('md')]: { padding: '0px 0px 16px 0px' } },
        navigationButtonContainer: { display: 'flex', alignItems: 'center' },
        navigationIconContainer: {
            width: '32px',
            height: '32px',
            background: '#F2F2F4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { cursor: 'pointer' },
        },
        navIconLeft: { marginLeft: 11, width: 18 },
        navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        tier,
        brandEmployerId,
        profileData,
        selectedJob,
        setSelectedJob,
        activeJobCount,
        setActiveJobCount,
        handleChangeMenu,
        showDetails,
        setShowDetails,
        searchedPerformed,
        setSearchPerformed,
    } = params;

    // const activeJobsCount = _get(profileData, 'activeJobsCount', 0);
    const companyInfoData = _get(profileData, 'info');
    const brandData = _get(profileData, 'brand');
    const socialMediaData = _get(profileData, 'social');

    // const { employer } = props;

    const companyName = _get(companyInfoData, 'companyName');
    // const isEmpBadge = _get(companyInfoData, 'isEmpBadge');
    // const profilePicUrl = _get(brandData, 'profilePicUrl');

    const pageTitle = `${PAGE_TITLE_BRAND_PAGE} | ${companyName}`;

    const defns = {
        pageHead: { title: pageTitle },
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        tier3Buttons: {
            isHidden: activeJobCount === 0 || tier !== 3,
        },
        companyProfileSection: {
            container: true,
            // style: {
            //     display: selectedMenu !== 'info' && 'none',
            // },
        },
        companyActiveJobsSection: {
            container: true,
            // style: {
            //     display: selectedMenu !== 'job' && 'none',
            // },
        },
        companyInfoContainer: { container: true, lg: 4, md: 4, sm: 12, xs: 12 },
        jobListContainer: { container: true, xl: 4, lg: 12, md: 12, sm: 12, xs: 12 },
        companyBioContainer: { container: true, lg: 8, md: 8, sm: 12, xs: 12 },
        jobInfoContainer: { container: true, xl: 8, lg: 12, md: 12, sm: 12, xs: 12 },
        navigation: { container: true, direction: 'row' },
        navigationItem: { item: true, xl: 3, lg: 4, md: 11, sm: 12, xs: 12 },
        showcase: {
            brandData,
            companyInfoData,
            // cover: 'https://i1.wp.com/coverfiles.alphacoders.com/755/75543.jpg',
            // logo: 'https://cdn.mos.cms.futurecdn.net/xDGQ9dbLmMpeEqhiWayMRB.jpg',
            // companyName: 'McDonald’d Corporation',
            // verified: true,
        },
        jobDetail: { job: selectedJob },
        companyInfo: {
            companyInfoData,
            socialMediaData,
        },
        companyBio: {
            companyInfoData,
        },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },
        infoButton: {
            onClick: () => handleChangeMenu('info'),
            // style: {
            //     backgroundColor: selectedMenu !== 'info' && '#00000000',
            //     color: selectedMenu === 'info' && 'black',
            // },
        },
        jobButton: {
            onClick: () => handleChangeMenu('job'),
            // style: {
            //     backgroundColor: selectedMenu !== 'job' && '#00000000',
            //     color: selectedMenu === 'job' && 'black',
            // },
        },
        jobNumberBadge: {
            color: 'primary',
            badgeContent: activeJobCount,
        },
        activeJobsSideBar: {
            selectedJob,
            setSelectedJob,
            setBrandPageActiveJobCount: setActiveJobCount,
            // brandPageActiveJobs: activeJobs,
            // setBrandPageActiveJobs: setActiveJobs,
            query: { employerId: brandEmployerId, sortTag: { sortColumn: ELASTICSEARCH_SORT_COLUMN_UPDATE_DATE, sortOrder: ELASTICSEARCH_SORT_ORDER_DESC } },
            setShowDetails,
            showDetails,
            searchedPerformed,
            setSearchPerformed,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const Tier3Buttons = props => {
    const { defns } = props;
    const isHidden = _get(defns, 'tier3Buttons.isHidden', false);

    return isHidden ? null : (
        <div {...defns.tier3Buttons}>
            <AfitdButton {...defns.infoButton}>Company Info</AfitdButton>
            <AfitdButton {...defns.jobButton}>
                Active Jobs <Badge {...defns.jobNumberBadge}></Badge>
            </AfitdButton>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const BrandPage = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    // const appContext = useContext(AppContext);
    // const { currentEmployer } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { profileData /*, setSnackbarMessage, setSnackbarErrorMessage*/ } = layoutContext;
    const profilePicUrl = _get(profileData, 'brand.profilePicUrl');
    const bannerUrl = _get(profileData, 'brand.bannerUrl');

    const { tabId } = useParams();

    const theme = useTheme();
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { employer } = props;
    const brandEmployerId = _get(employer, 'uid');
    const tier = _get(employer, 'tier', 1);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [selectedMenu, setSelectedMenu] = useState(tabId === BRAND_PAGE_TAB_ID_JOBS ? 'job' : 'info');
    // const [activeJobs, setActiveJobs] = useState();
    const [activeJobCount, setActiveJobCount] = useState();
    const [selectedJob, setSelectedJob] = useState();
    const [showDetails, setShowDetails] = useState(false);
    const [searchedPerformed, setSearchPerformed] = React.useState(false);
    const [searchResultTotal, setSearchResultTotal] = useState();

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_BRAND_PAGE);
    }, []);

    // Do NOT call loadActiveJobsByEmployerId because it can potentially have a lot of Firestore reads -el 20221012
    // // load active jobs if tier 3
    // useEffect(() => {
    //     if (tier === 3 && !activeJobs) {
    //         if (brandEmployerId) {
    //             loadActiveJobsByEmployerId({
    //                 employerId: brandEmployerId,
    //                 setData: setActiveJobs,
    //             });
    //         }
    //     }
    // }, [tier, activeJobs, brandEmployerId]);

    // Let the SideBar set the selectedJob -el 20221012
    // // initialize selected job when activeJobs is loaded
    // useEffect(() => {
    //     if (!selectedJob && _size(activeJobs) > 0) {
    //         setSelectedJob(activeJobs[0]);
    //     }
    // }, [activeJobs, selectedJob]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isActiveJobsView = selectedMenu === 'job';
    const isLogoVisible = !!profilePicUrl;
    const isBannerVisible = tier !== 1 && !!bannerUrl;

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeMenu = item => {
        setSelectedMenu(item);
    };
    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isBannerVisible, isLogoVisible, isActiveJobsView })();
    const defns = getDefns({
        classes,
        props,
        tier,
        brandEmployerId,
        profileData,
        selectedJob,
        setSelectedJob,
        activeJobCount,
        setActiveJobCount,
        handleChangeMenu,
        showDetails,
        setShowDetails,
        searchedPerformed,
        setSearchPerformed,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...layoutContext, isBannerVisible, isLogoVisible, isBrandPage: true, brandEmployerId, searchResultTotal, setSearchResultTotal }}>
            <AfitdPageHead {...defns.pageHead} />
            <div {...defns.root}>
                {showDetails && lgMatches ? null : (
                    <div>
                        <CompanyShowcase {...defns.showcase} />
                        <Tier3Buttons defns={defns} />
                        <Grid {...defns.companyProfileSection}>
                            <Grid {...defns.companyInfoContainer}>
                                <CompanyInfo {...defns.companyInfo} />
                            </Grid>
                            <Grid {...defns.companyBioContainer}>
                                <CompanyBioBenefit {...defns.companyBio} />
                            </Grid>
                        </Grid>
                    </div>
                )}

                <Grid {...defns.companyActiveJobsSection}>
                    {/* <Grid {...defns.jobListContainer}>
                        <ActiveJobsSideBar {...defns.activeJobsSideBar} />
                    </Grid>
                    <Grid {...defns.jobInfoContainer}>
                        <JobDetail {...defns.jobDetail} />
                    </Grid> */}
                    {showDetails ? (
                        <div {...defns.jobInfoContainerSection}>
                            <Grid {...defns.navigation}>
                                <Grid {...defns.navigationItem}>
                                    <div {...defns.navigationButtonContainer} onClick={() => setShowDetails(false)}>
                                        <div {...defns.navigationIconContainer}>
                                            <ArrowBackIosIcon {...defns.navIconLeft} />
                                        </div>
                                        <span {...defns.navigationText}>Back</span>
                                    </div>
                                </Grid>
                            </Grid>
                            <JobDetail {...defns.jobDetail} />
                        </div>
                    ) : (
                        <Grid {...defns.jobListContainer}>{tier === 3 && <ActiveJobsSideBar {...defns.activeJobsSideBar} />}</Grid>
                    )}

                    {lgMatches ? null : (
                        <Grid {...defns.jobInfoContainer}>
                            <JobDetail {...defns.jobDetail} />
                        </Grid>
                    )}
                </Grid>
            </div>
        </LayoutContext.Provider>
    );
};

export default BrandPage;
